import { useEffect, useRef } from 'react'

/**
 * Whether an Emergency Help screen has already rendered in THIS page load.
 * Module state, so a reload resets it. (React Router's `location.key` is not
 * enough: browser history keeps it across a reload, so a refreshed screen
 * would look like an in-app arrival. Review of M35-T06.)
 */
let aScreenHasRendered = false

/**
 * Move focus to a screen's heading when the person ARRIVES from another
 * Emergency Help screen (M35-T06). The link they pressed has just unmounted;
 * without this, focus falls to <body> and a screen-reader user hears nothing.
 *
 * Not on the first screen of a page load (a fresh visit or a reload): then
 * nothing is focused, so the first Tab still lands on the header's 112, which
 * must be the first control a person reaches.
 *
 * `ready` waits for a heading that appears only once data has loaded.
 */
export function useArrivalFocus<T extends HTMLElement>(ready = true) {
  const ref = useRef<T>(null)
  // Read once, on this screen's first render, before marking the page.
  const arrivedFromAnotherScreen = useRef(aScreenHasRendered)
  useEffect(() => {
    aScreenHasRendered = true
  }, [])
  useEffect(() => {
    if (ready && arrivedFromAnotherScreen.current) ref.current?.focus()
  }, [ready])
  return ref
}
